const CAP_CRIPTOMONEDAS = {
  title: "¿Qué es una criptomoneda?",
  sections: [
    {
      heading: "Introducción: una nueva forma de dinero digital",
      text: [
        "Una **criptomoneda** es un tipo de moneda completamente digital que fue creada para funcionar sin depender de bancos, gobiernos u otras entidades centrales que emitan y controlen el dinero.",
        "No existe en forma física (no hay billetes ni monedas metálicas); vive únicamente en internet y puede enviarse a cualquier parte del mundo en cuestión de segundos, sin intermediarios y sin pedir permiso a nadie.",
        "Al igual que el euro, el dólar u otras monedas tradicionales, una criptomoneda puede usarse como medio de intercambio para comprar bienes y servicios, ahorrar, invertir o realizar pagos entre personas.",
        "La gran diferencia es que, en lugar de apoyarse en un banco central, se apoya en **matemáticas, criptografía y redes distribuidas**."
      ]
    },
    {
      heading: "¿En qué se parece al dinero tradicional?",
      text: [
        "En muchos aspectos, una criptomoneda **funciona como cualquier otra moneda**:",
        "Puedes usarla para pagar productos o servicios, enviarla a otras personas o mantenerla como ahorro a largo plazo.",
        "Su valor sube o baja según la oferta y la demanda, igual que ocurre con las divisas tradicionales o con activos como el oro."
      ],
      list: [
        "Se puede intercambiar por bienes y servicios.",
        "Puede guardarse como forma de ahorro o inversión.",
        "Puede cambiarse por otras monedas (cripto o fíat)."
      ]
    },
    {
      heading: "¿En qué se diferencia del dinero fiduciario (fíat)?",
      text: [
        "Aunque comparte algunas funciones con el dinero tradicional, las criptomonedas introducen cambios profundos en cómo se crea, se registra y se transfiere el valor.",
        "A continuación veremos las diferencias clave que las hacen tan especiales."
      ],
      list: [
        "El dinero fíat lo emiten y controlan gobiernos y bancos centrales.",
        "Las criptomonedas surgen de un protocolo y reglas matemáticas.",
        "En el dinero tradicional, el registro de saldos suele estar en bases de datos privadas.",
        "En la mayoría de criptomonedas, el registro es público y distribuido."
      ]
    },
    {
      heading: "1) No dependen de gobiernos ni bancos centrales",
      text: [
        "La mayoría de las criptomonedas conocidas (como Bitcoin o Ethereum) funcionan en redes **descentralizadas**.",
        "Esto significa que ningún gobierno, banco o empresa tiene control total sobre la moneda o puede cambiar sus reglas de forma unilateral.",
        "En lugar de un servidor central, miles de ordenadores repartidos por el mundo (nodos) mantienen y validan el sistema."
      ],
      list: [
        "🔹 No hay un banco que pueda congelar tu saldo por decisión propia.",
        "🔹 Las reglas básicas (emisión, validación, consenso) están en el protocolo.",
        "🔹 Cambiar esas reglas requiere la coordinación de la comunidad, no de una sola institución.",
        "🔹 Excepción importante: las **CBDC** (monedas digitales de bancos centrales) sí son emitidas y controladas por un banco central, aunque sean digitales."
      ]
    },
    {
      heading: "2) Su valor depende de la confianza y la utilidad",
      text: [
        "El dinero tradicional tiene valor porque un gobierno lo declara de curso legal y porque la población confía en que podrá usarlo para pagar impuestos y comprar bienes.",
        "En las criptomonedas, el valor surge de varios factores combinados:"
      ],
      list: [
        "🔹 La confianza de los usuarios en la tecnología y en las reglas del sistema.",
        "🔹 La utilidad real: si se usa para pagos, ahorro, contratos inteligentes, aplicaciones, etc.",
        "🔹 La escasez programada (por ejemplo, el límite de 21 millones de bitcoins).",
        "🔹 La oferta y la demanda en los mercados de intercambio."
      ]
    },
    {
      heading: "3) Uso de criptografía para la seguridad",
      text: [
        "Las criptomonedas utilizan **criptografía avanzada** para proteger las transacciones y los saldos.",
        "En lugar de nombres y apellidos, se emplean direcciones, claves públicas y firmas digitales que permiten comprobar que una persona es dueña de sus fondos sin revelar su identidad real.",
        "La criptografía hace que las operaciones sean muy difíciles de falsificar o modificar."
      ],
      list: [
        "🔹 Cada transacción se firma digitalmente con una clave privada.",
        "🔹 Cualquiera puede verificar la firma con la clave pública correspondiente.",
        "🔹 Alterar una transacción ya confirmada implicaría romper la criptografía y el consenso de toda la red, algo prácticamente imposible con la tecnología actual."
      ]
    },
    {
      heading: "4) Código abierto, auditable y transparente",
      text: [
        "La mayoría de las criptomonedas se desarrollan como **proyectos de código abierto**.",
        "Esto significa que el software que define cómo funciona la red está disponible públicamente y cualquier persona puede revisarlo, auditarlo o proponer mejoras."
      ],
      list: [
        "🔹 Cualquier desarrollador puede analizar el código para detectar errores o puertas traseras.",
        "🔹 Las reglas del sistema no están ocultas en una caja negra bancaria.",
        "🔹 Pueden surgir nuevas criptomonedas a partir del código de otras (bifurcaciones o forks)."
      ]
    },
    {
      heading: "5) Un libro contable distribuido: la blockchain",
      text: [
        "Detrás de la mayoría de las criptomonedas hay una **blockchain** o cadena de bloques.",
        "La blockchain es como un libro contable gigante donde se registran todas las transacciones en orden cronológico.",
        "La diferencia clave es que este libro no está en un solo ordenador, sino copiado y sincronizado en miles de nodos alrededor del mundo."
      ],
      list: [
        "🔹 Cada bloque contiene un conjunto de transacciones.",
        "🔹 Cada bloque está enlazado criptográficamente con el anterior, formando una cadena.",
        "🔹 Una vez que una transacción entra en un bloque confirmado, se considera inmutable.",
        "🔹 Cualquier persona puede consultar el historial, pero nadie puede borrar o cambiar lo ya registrado."
      ]
    },
    {
      heading: "Ventajas principales de las criptomonedas",
      text: [
        "Las criptomonedas no son perfectas, pero sí aportan ventajas claras frente a los sistemas tradicionales, especialmente en contextos digitales y globales."
      ],
      list: [
        "🔹 Pagos globales casi instantáneos, sin necesidad de bancos intermediarios.",
        "🔹 Mayor control personal sobre los fondos (si tú guardas tus claves, tú mandas).",
        "🔹 Transparencia: las transacciones pueden verificarse públicamente en la blockchain.",
        "🔹 Accesibilidad: basta con un teléfono e internet para participar.",
        "🔹 Innovación constante: contratos inteligentes, DeFi, NFT, Web3 y nuevos modelos de negocio."
      ]
    },
    {
      heading: "Retos y puntos a tener en cuenta",
      text: [
        "Junto con sus ventajas, las criptomonedas traen también desafíos que es importante conocer para usarlas con criterio.",
        "Algunos de los más frecuentes son:"
      ],
      list: [
        "🔹 Alta volatilidad: el precio puede subir o bajar de forma brusca.",
        "🔹 Riesgo de pérdida si se extravían las claves privadas o la frase semilla.",
        "🔹 Estafas y proyectos poco serios que intentan aprovecharse de la falta de conocimiento.",
        "🔹 Curva de aprendizaje: entender bien cómo funcionan lleva tiempo y práctica."
      ]
    },
    {
      heading: "Cierre: el punto de partida hacia Bitcoin",
      text: [
        "Las criptomonedas representan una nueva forma de entender el dinero: **más digital, más programable y menos dependiente de intermediarios tradicionales**.",
        "En este capítulo hemos visto qué son, en qué se diferencian del dinero fíat, qué papel juega la criptografía y por qué la blockchain es tan importante.",
        "En la siguiente lección conoceremos con más detalle a **Bitcoin**, la primera y más conocida criptomoneda, y veremos por qué muchos la consideran el equivalente digital del oro.",
        "Antes de continuar, es muy útil que respondas un breve cuestionario para comprobar qué tanto has entendido hasta ahora. Ese pequeño ejercicio te ayudará a detectar qué conceptos ya dominas y cuáles conviene repasar."
      ]
    }
  ]
};

export default CAP_CRIPTOMONEDAS;
